// Intentionally keeping this file lint-clean: avoid direct console usage.
/**
 * Audio Recorder Module with Silence Detection and Smart Chunking
 *
 * This module provides audio recording functionality with:
 * - Real-time silence detection using AudioWorklet
 * - Smart chunking based on silence, duration, or fixed intervals
 * - MediaRecorder with independent chunks (each with proper headers)
 * - Configurable audio quality and chunk parameters
 */

// ============================================================================
// Types and Interfaces
// ============================================================================

/**
 * Configuration options for the audio recorder
 */
export interface AudioRecorderConfig {
  /** Target sample rate for silence detection (Hz) */
  targetSampleRate?: number
  /** Number of audio channels (1 for mono, 2 for stereo) */
  channels?: number
  /** RMS threshold to start counting silence */
  silenceThresholdLow?: number
  /** RMS threshold to reset silence counter if exceeded */
  silenceThresholdHigh?: number
  /** Minimum silence duration to trigger chunk creation (seconds) */
  minSilenceDuration?: number
  /** Minimum chunk duration (seconds) */
  minChunkDuration?: number
  /** Maximum chunk duration (seconds) */
  maxChunkDuration?: number
  /** MediaRecorder timeslice interval (milliseconds) */
  mediaRecorderTimeslice?: number
  /** How often to check if a new chunk is needed (milliseconds) */
  chunkCheckInterval?: number
  /** Preferred audio MIME type for MediaRecorder */
  preferredMimeType?: string
  /** Audio bits per second for MediaRecorder */
  audioBitsPerSecond?: number
}

/**
 * Represents a silence event detected during recording
 */
export interface SilenceEvent {
  /** Start time of silence period (seconds from recording start) */
  startTime: number
  /** End time of silence period (seconds from recording start) */
  endTime: number
  /** Duration of silence period (seconds) */
  duration: number
}

/**
 * Represents a completed audio chunk
 */
export interface AudioChunk {
  /** Unique identifier for this chunk */
  id: number
  /** Audio data as Blob */
  blob: Blob
  /** Duration of the chunk (seconds) */
  duration: number
  /** MIME type of the audio data */
  mimeType: string
  /** Reason why this chunk was created */
  reason: string
  /** Timestamp when chunk was created */
  timestamp: Date
}

/**
 * Callback function types
 */
export type OnChunkCallback = (chunk: AudioChunk) => void
export type OnSilenceCallback = (event: SilenceEvent) => void
export type OnErrorCallback = (error: Error) => void

// ============================================================================
// AudioWorklet Processor Code
// ============================================================================

/**
 * AudioWorklet processor code for silence detection
 * This runs in a separate thread and analyzes audio samples in real-time
 */
const SILENCE_DETECTOR_WORKLET = `
class SilenceDetectorProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.isRecording = false;

    this.port.onmessage = (event) => {
      if (event.data.command === 'start') {
        this.isRecording = true;
      } else if (event.data.command === 'stop') {
        this.isRecording = false;
      }
    };
  }

  process(inputs) {
    if (!this.isRecording) return true;

    const input = inputs[0];
    if (input && input[0]) {
      // Send audio samples to main thread for silence analysis
      this.port.postMessage({
        type: 'samples',
        data: input[0]
      });
    }

    return true; // Keep processor alive
  }
}

registerProcessor('silence-detector', SilenceDetectorProcessor);
`

// ============================================================================
// Main AudioRecorder Class
// ============================================================================

/**
 * AudioRecorder class that handles recording with silence detection and smart chunking
 *
 * @example
 * ```typescript
 * const recorder = new AudioRecorder({
 *   minSilenceDuration: 0.3,
 *   maxChunkDuration: 10
 * });
 *
 * recorder.onChunk = (chunk) => {
 *   console.log('New chunk:', chunk);
 *   // Upload chunk to server, update UI, etc.
 * };
 *
 * await recorder.initialize();
 * await recorder.startRecording();
 * // ... later
 * await recorder.stopRecording();
 * ```
 */
export class AudioRecorder {
  // Configuration
  private config: Required<AudioRecorderConfig>

  // Audio components
  private audioContext: AudioContext | null = null
  private audioWorkletNode: AudioWorkletNode | null = null
  private mediaRecorder: MediaRecorder | null = null
  private mediaStream: MediaStream | null = null

  // Recording state
  private isRecording = false
  private recordingStartTime = 0
  private currentChunkBlobs: Blob[] = []
  private currentChunkStartTime = 0
  private chunkCounter = 0
  private lastChunkReason = ''
  private isCreatingChunk = false

  // Silence tracking
  private silenceEvents: SilenceEvent[] = []
  private currentSilenceStart: number | null = null
  private consecutiveSilenceSamples = 0

  // Intervals
  private chunkCheckInterval: number | null = null

  // Callbacks
  public onChunk: OnChunkCallback | null = null
  public onSilence: OnSilenceCallback | null = null
  public onError: OnErrorCallback | null = null

  /**
   * Creates a new AudioRecorder instance
   * @param config - Configuration options
   */
  constructor(config: AudioRecorderConfig = {}) {
    // Set default configuration
    this.config = {
      targetSampleRate: 16000,
      channels: 1,
      silenceThresholdLow: 0.015,
      silenceThresholdHigh: 0.025,
      minSilenceDuration: 0.3,
      minChunkDuration: 3.0,
      maxChunkDuration: 8.0,
      mediaRecorderTimeslice: 100,
      chunkCheckInterval: 100,
      preferredMimeType: 'audio/webm;codecs=opus',
      audioBitsPerSecond: 48000,
      ...config,
    }
  }

  /**
   * Initializes the audio recorder by requesting microphone access and setting up audio processing
   * Must be called before starting recording
   *
   * @throws Error if microphone access is denied or audio setup fails
   */
  async initialize(stream: MediaStream): Promise<void> {
    try {
      if (!stream) {
        throw new Error('A MediaStream must be provided to initialize the recorder')
      }

      // Use the externally supplied MediaStream
      this.mediaStream = stream

      // Set up AudioContext for silence detection
      this.audioContext = new AudioContext()

      // Create and load AudioWorklet for silence detection
      const workletBlob = new Blob([SILENCE_DETECTOR_WORKLET], {
        type: 'application/javascript',
      })
      const workletUrl = URL.createObjectURL(workletBlob)

      await this.audioContext.audioWorklet.addModule(workletUrl)
      URL.revokeObjectURL(workletUrl)

      // Connect audio pipeline
      const source = this.audioContext.createMediaStreamSource(this.mediaStream)
      this.audioWorkletNode = new AudioWorkletNode(this.audioContext, 'silence-detector')
      source.connect(this.audioWorkletNode)

      // Set up message handler for silence detection
      this.audioWorkletNode.port.onmessage = (event) => {
        if (event.data.type === 'samples') {
          this.processSilenceDetection(event.data.data)
        }
      }
    } catch (error) {
      this.handleError(new Error(`Failed to initialize audio: ${error}`))
      throw error
    }
  }

  /**
   * Starts recording audio
   *
   * @param targetChunkDuration - Target duration for fixed-size chunks (seconds)
   * @throws Error if recorder is not initialized or already recording
   */
  async startRecording(targetChunkDuration = 3): Promise<void> {
    if (!this.mediaStream || !this.audioWorkletNode) {
      throw new Error('Recorder not initialized. Call initialize() first.')
    }

    if (this.isRecording) {
      throw new Error('Already recording')
    }

    // Reset state
    this.currentChunkBlobs = []
    this.chunkCounter = 0
    this.silenceEvents = []
    this.currentSilenceStart = null
    this.consecutiveSilenceSamples = 0
    this.isCreatingChunk = false

    // Start recording
    this.recordingStartTime = Date.now()
    this.isRecording = true

    // Start silence detection
    this.audioWorkletNode.port.postMessage({ command: 'start' })

    // Determine best available MIME type
    const mimeType = this.getBestMimeType()

    // Create initial MediaRecorder (omit mimeType option if none supported)
    this.mediaRecorder = mimeType
      ? new MediaRecorder(this.mediaStream, {
          mimeType,
          audioBitsPerSecond: this.config.audioBitsPerSecond,
        })
      : new MediaRecorder(this.mediaStream, { audioBitsPerSecond: this.config.audioBitsPerSecond })
    this.setupMediaRecorder()

    this.mediaRecorder.start(this.config.mediaRecorderTimeslice)
    this.currentChunkStartTime = this.getTimestamp()

    // Start chunk checking interval
    this.chunkCheckInterval = window.setInterval(() => {
      this.checkForNewChunk(targetChunkDuration)
    }, this.config.chunkCheckInterval)
  }

  /**
   * Stops recording and finalizes any remaining audio data
   *
   * @returns Promise that resolves when recording is fully stopped
   */
  async stopRecording(): Promise<void> {
    if (!this.isRecording) {
      throw new Error('Not currently recording')
    }

    this.isRecording = false

    // Stop intervals
    if (this.chunkCheckInterval) {
      clearInterval(this.chunkCheckInterval)
      this.chunkCheckInterval = null
    }

    // Stop silence detection
    if (this.audioWorkletNode) {
      this.audioWorkletNode.port.postMessage({ command: 'stop' })
    }

    // Stop MediaRecorder and finalize last chunk
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop()

      // Wait for stop event
      await new Promise<void>((resolve) => {
        if (this.mediaRecorder) {
          this.mediaRecorder.addEventListener('stop', () => resolve(), { once: true })
        } else {
          resolve()
        }
      })

      // Finalize last chunk
      if (this.currentChunkBlobs.length > 0) {
        const chunk = this.createChunkFromCurrentBlobs('Recording stopped')
        if (chunk && this.onChunk) {
          this.onChunk(chunk)
        }
      }
    }
  }

  /**
   * Cleans up all resources used by the recorder
   * Should be called when the recorder is no longer needed
   */
  cleanup(): void {
    this.stopRecording().catch(() => {}) // Ignore errors if not recording

    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }

    // Do NOT stop tracks; they are managed by the caller.
    this.mediaStream = null

    this.audioWorkletNode = null
    this.mediaRecorder = null
  }

  /**
   * Gets the current recording timestamp in seconds
   * @private
   */
  private getTimestamp(): number {
    return this.isRecording ? (Date.now() - this.recordingStartTime) / 1000 : 0
  }

  /**
   * Calculates RMS (Root Mean Square) of audio samples for volume detection
   * @private
   */
  private calculateRMS(samples: Float32Array): number {
    let sum = 0
    for (const sample of samples) {
      sum += sample * sample
    }
    return Math.sqrt(sum / samples.length)
  }

  /**
   * Processes audio samples to detect silence events
   * @private
   */
  private processSilenceDetection(samples: Float32Array): void {
    const level = this.calculateRMS(samples)
    const currentTime = this.getTimestamp()

    if (level < this.config.silenceThresholdLow) {
      this.consecutiveSilenceSamples += samples.length

      // Start new silence period if not already in one
      if (this.currentSilenceStart === null) {
        this.currentSilenceStart = currentTime
      }
    } else if (level > this.config.silenceThresholdHigh) {
      // End silence period if we were in one
      if (this.currentSilenceStart !== null) {
        const silenceDuration = currentTime - this.currentSilenceStart

        if (silenceDuration >= this.config.minSilenceDuration) {
          const event: SilenceEvent = {
            startTime: this.currentSilenceStart,
            endTime: currentTime,
            duration: silenceDuration,
          }

          this.silenceEvents.push(event)

          if (this.onSilence) {
            this.onSilence(event)
          }
        }

        this.currentSilenceStart = null
      }

      this.consecutiveSilenceSamples = 0
    }
  }

  /**
   * Determines if a new chunk should be created based on current conditions
   * @private
   */
  private shouldCreateNewChunk(targetChunkDuration: number): boolean {
    const currentTime = this.getTimestamp()
    const chunkDuration = currentTime - this.currentChunkStartTime

    // Check for minimum duration
    if (chunkDuration < this.config.minChunkDuration) {
      return false
    }

    // Check for maximum duration
    if (chunkDuration >= this.config.maxChunkDuration) {
      this.lastChunkReason = `Maximum duration (${chunkDuration.toFixed(1)}s)`
      return true
    }

    // Check for silence events
    const recentSilence = this.silenceEvents.find(
      (event) =>
        event.endTime > this.currentChunkStartTime &&
        event.startTime < currentTime &&
        event.duration >= this.config.minSilenceDuration
    )

    if (recentSilence) {
      this.lastChunkReason = `Prolonged silence (${recentSilence.duration.toFixed(2)}s)`
      return true
    }

    // Check configured chunk duration
    if (chunkDuration >= targetChunkDuration) {
      this.lastChunkReason = `Fixed-size chunk (${chunkDuration.toFixed(1)}s)`
      return true
    }

    return false
  }

  /**
   * Checks if a new chunk should be created and creates it if needed
   * @private
   */
  private checkForNewChunk(targetChunkDuration: number): void {
    if (!this.isRecording || this.isCreatingChunk) return

    if (this.shouldCreateNewChunk(targetChunkDuration)) {
      this.createNewChunk()
    }
  }

  /**
   * Creates a new chunk from current audio data and starts a new recording
   * @private
   */
  private async createNewChunk(): Promise<void> {
    if (this.isCreatingChunk || !this.isRecording) return
    this.isCreatingChunk = true
    try {
      // Stop current recorder if exists
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop()

        // Wait for the stop event
        await new Promise<void>((resolve) => {
          if (this.mediaRecorder) {
            this.mediaRecorder.addEventListener('stop', () => resolve(), { once: true })
          } else {
            resolve()
          }
        })
      }

      // Create chunk from current blobs
      const chunk = this.createChunkFromCurrentBlobs(this.lastChunkReason)
      if (chunk && this.onChunk) {
        this.onChunk(chunk)
      }

      // Clean up old silence events (keep last 30s)
      const cutoffTime = this.getTimestamp() - 30
      this.silenceEvents = this.silenceEvents.filter((e) => e.endTime > cutoffTime)

      // Create new MediaRecorder if still recording
      if (this.isRecording && this.mediaStream) {
        try {
          // Check if MediaStream is still valid
          const audioTracks = this.mediaStream.getAudioTracks()
          const firstTrack = audioTracks[0]
          if (audioTracks.length === 0 || firstTrack?.readyState !== 'live') {
            throw new Error(
              `MediaStream is no longer valid: ${audioTracks.length} tracks, readyState: ${firstTrack?.readyState}`
            )
          }

          // Small delay to ensure previous recorder is fully stopped
          await new Promise((resolve) => setTimeout(resolve, 10))

          const mimeType = this.getBestMimeType()

          this.mediaRecorder = mimeType
            ? new MediaRecorder(this.mediaStream, {
                mimeType,
                audioBitsPerSecond: this.config.audioBitsPerSecond,
              })
            : new MediaRecorder(this.mediaStream, {
                audioBitsPerSecond: this.config.audioBitsPerSecond,
              })

          this.setupMediaRecorder()

          this.mediaRecorder.start(this.config.mediaRecorderTimeslice)

          this.currentChunkStartTime = this.getTimestamp()
        } catch (error) {
          this.handleError(new Error(`Failed to restart MediaRecorder: ${error}`))
          this.isRecording = false // Stop recording if we can't continue
          throw error
        }
      }
    } finally {
      this.isCreatingChunk = false
    }
  }

  /**
   * Sets up MediaRecorder event handlers
   * @private
   */
  private setupMediaRecorder(): void {
    if (!this.mediaRecorder) return

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.currentChunkBlobs.push(event.data)
      }
    }

    this.mediaRecorder.onerror = (event) => {
      this.handleError(new Error(`MediaRecorder error: ${event}`))
    }
  }

  /**
   * Creates an AudioChunk from current blob data
   * @private
   */
  private createChunkFromCurrentBlobs(reason: string): AudioChunk | null {
    if (this.currentChunkBlobs.length === 0) return null

    const mimeType = this.mediaRecorder?.mimeType || 'audio/webm'
    const blob = new Blob(this.currentChunkBlobs, { type: mimeType })
    const duration = this.getTimestamp() - this.currentChunkStartTime

    this.chunkCounter++
    this.currentChunkBlobs = []

    const result: AudioChunk = {
      id: this.chunkCounter,
      blob,
      duration,
      mimeType,
      reason,
      timestamp: new Date(),
    }

    return result
  }

  /**
   * Determines the best available MIME type for MediaRecorder
   * @private
   */
  private getBestMimeType(): string | null {
    const types = [
      this.config.preferredMimeType,
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg',
    ]

    for (const type of types) {
      if (type && MediaRecorder.isTypeSupported(type)) {
        return type
      }
    }

    // No supported type found; let the browser decide by returning null
    return null
  }

  /**
   * Handles errors by calling the error callback if set
   * @private
   */
  private handleError(error: Error): void {
    if (this.onError) {
      this.onError(error)
    }
  }
}
